import { eqShallow } from '@ipfabric/utils/fp/comparison';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import memoizeOne from 'memoize-one';
import { useMemo } from 'react';
import { useKeepIdentityIfEqual } from 'utils/hooks/useKeepIdentityIfEqual';
import { type DraftStateUpdater, useStateProxy } from 'utils/react/StateProxy';
import type { SelectorCache } from 'utils/react/scopedSelector';
import {
  type CommandDispatcher,
  type CommandHandlerFactory,
  useCommandDispatcher,
} from 'utils/react/useCommandDispatcher';

// Generic arguments legend (I decided for short notation as they are heavily repeated):
// S = inner model state
// O = outer state
// C = additional command context
// @see useCommandModel for details

/**
 * Combined inner + outer state + selector cache to allow using scoped selectors on the model
 *
 * It is used as:
 * - exact result of `getState()` function provided via command context
 * - part of `CommandModel` type provided by the hook to React components
 */
export type CommandModelMergedState<S, O> = S &
  O & {
    selectorCache: SelectorCache;
  };

/**
 * All command models provide state management functions (getState, updateState)
 * to all commands. Every specific command model can be provided with additional resources
 * described by the `C` generic argument.
 */
export type CommandModelCommandContext<S, C, O> = C & {
  getState: () => CommandModelMergedState<S, O>;
  updateState: DraftStateUpdater<S>;
};

/**
 * Type of the `dispatch` function provided by the model to call commands
 */
export type CommandModelCommandDispatcher<S, C, O> = CommandDispatcher<
  CommandModelCommandContext<S, C, O>
>;

/**
 * Result of `useCommandModel` hook
 */
export type CommandModel<S, C, O> = CommandModelMergedState<S, O> & {
  dispatch: CommandModelCommandDispatcher<S, C, O>;
};

/**
 * Type of the higher-order command function that receives command context at first order call
 * and command arguments (optionally) in second order call
 */
export type CommandModelCommandFactory<
  S,
  C,
  O,
  TCommandArgs = never,
> = CommandHandlerFactory<CommandModelCommandContext<S, C, O>, TCommandArgs>;

/**
 * Create a complex component local state manager
 *
 * This hook integrates these tools together:
 *   - useStateProxy
 *   - useCommandDispatcher
 *   - SelectorCache
 *
 * It is recommended to be used with scoped selectors:
 * - createScopedSelector to build selectors
 * - Propagate result of the hook via React context, then consume in components
 *   with useContextSelectors()
 *
 * The component creates a state managed with useStateProxy() and merges this state
 * with provided outerState argument and an instance of SelectorCache to support memoization
 * of scoped selectors.
 *
 * The state can be controlled by commands using useCommandDispatcher() logic.
 * `getState` (that provides inner state + outer state + selector cache) and `updateState`
 * are automatically provided in command context, plus you can provide additional
 * dependencies via additionalCommandContext parameter.
 *
 * First, declare your types:
 * ```
 * export interface ZooState {
 *   animals: string[];
 * }
 * export interface ZooAdditionalCommandContext {
 *   loadRandomAnimal: () => string;
 * }
 * export interface ZooOuterState {
 *   weather: 'good' | 'bad';
 * }
 * ```
 *
 * You can get a handful of useful types that fit your particular model by using
 * generics from this module to avoid repeating the complex generic heavily
 * in your component. There is currently no better way to declare a bunch
 * of types from a single generic argument set including generic result types.
 * ```
 * // I recommend to create local type aliases to avoid heavy repeating
 * type S = ZooState;
 * type C = ZooAdditionalCommandContext;
 * type O = ZooOuterState;
 *
 * // the type of context argument provided to first-order call to commands:
 * export type ZooCommandContext = CommandModelCommandContext<S, C, O>;
 * // type of a useCommandDispatcher-shaped command
 * export type ZooCommandFactory<TCommandArgs = never> = CommandModelCommandFactory<S, C, O, TCommandArgs>;
 * // type of `model.dispatch`
 * export type ZooDispatch = CommandModelCommandDispatcher<S, C, O>;
 * // type of `commandContext.getState()` (also a supertype of CommandModel<S, C, O>)
 * export type ZooMergedState = CommandModelMergedState<S, O>;
 * // type of the whole hook result
 * export type ZooModel = CommandModel<S, C, O>;
 * ```
 *
 * Note: as `CommandModel<S, C, O>` is a subtype of `CommandModelMergedState<S, O>`,
 * you can write selectors that work only with the merged state and use those selectors
 * both inside commands and in model consumer components (simply put, in useContextSelectors()).
 *
 * You can also have selectors that work with the whole command model (e.g.
 * `useContextSelectors({dispatch: prop('dispatch')})` to get the dispatch function in a component),
 * but such selectors won't obviously work inside commands.
 *
 * Now, example of minimal usage:
 * ```
 * const zooModel = useCommandModel<
 *   ZooState,
 *   ZooAdditionalCommandContext,
 *   ZooOuterState,
 * >(
 *   {animals: []},
 *   {loadAnimal: getRandomElement(['elephant', 'giraffe', 'camel'])},
 *   {weather: props.weather},
 * );
 *
 * const importAnimal: ZooCommandFactory = cmdContext => () => {
 *   cmdContext.updateState(draft => {
 *     draft.animals.push(cmdContext.loadAnimal());
 *   });
 * };
 *
 * interface AdmireAnimalCommand {
 *   animal: string;
 * }
 * const admireAnimal: ZooCommandFactory<AdmireAnimalCommand> = cmdContext => command => {
 *   if (cmdContext.getState().weather === 'bad') {
 *     console.log('I see nothing, all the animals are in shelters');
 *   } else if (cmdContext.getState().animals.includes(command.animal)) {
 *     console.log(`What a beautiful ${command.animal}!`);
 *   } else {
 *     console.log(`Oh, this ZOO doesn't have ${command.animal}s :(`);
 *   }
 * }
 * ```
 *
 * Note: although you may not need all the arguments, optionality is not implemented
 * to keep the typings around reasonably simple. Pass in empty object {} to whatever argument you don't
 * care about.
 *
 * additionalCommandContext and outerProps are memoized by attributes shallow equality
 * (similar to React component props), so you don't need to worry about memoizing the whole objects.
 *
 * @param initialState initial inner state or lazy initialization function
 * @param additionalCommandContext object that will be added to CommandModelCommandContext
 *   as extra dependencies of commands - functions or mostly stable objects, e.g.
 *   api request handlers, localization etc. It is a "simplified dependency injection",
 *   since actual dependency injection that gives only required dependencies to each command
 *   1) is tricky to implement without runtime typing, 2) needs some boilerplate we want to avoid.
 *   It should be an object with as much stable fields as possible - every change
 *   will purge memoized first-order calls to command factories (see useCommandDispatcher
 *   for details). These values won't be exposed to model consumers.
 * @param outerState this object will be merged with inner state (S) and provided
 *   via getState() both to commands and model consumers (React components).
 *   You can use this to make external dynamic values not managed by this model available
 *   to commands and selectors. Changes of these values will trigger component updates / purge memoizations
 *   in the same way as inner state, so it is a safe way to provide dynamic values.
 * @param onStateUpdate allowing to observe internal state updates.
 */
export const useCommandModel = <S, C, O>(
  initialState: S | (() => S),
  additionalCommandContext: C,
  outerState: O,
  options?: {
    onStateUpdate?: (nextState: S, prevState: S) => void;
  },
): CommandModel<S, C, O> => {
  const stableAdditionalCommandContext = useKeepIdentityIfEqual(
    eqShallow,
    additionalCommandContext,
  );
  const stableOuterState = useKeepIdentityIfEqual(eqShallow, outerState);
  const [getLocalState, updateStateRaw] = useStateProxy(initialState);
  const updateState: typeof updateStateRaw = useStableCallback(
    options?.onStateUpdate
      ? (...args) => {
          const prevState = getLocalState();
          updateStateRaw(...args);
          const nextState = getLocalState();
          options.onStateUpdate!(nextState, prevState);
        }
      : updateStateRaw,
  );

  const selectorCache = useMemo(() => new Map(), []);
  const getMergedState = useMemo(
    () =>
      memoizeOne((local: S, outer: O) => ({
        ...local,
        ...outer,
        selectorCache,
      })),
    [selectorCache],
  );
  const getState = useStableCallback(() =>
    getMergedState(getLocalState(), stableOuterState),
  );
  const context = useMemo(
    () => ({
      ...stableAdditionalCommandContext,
      getState,
      updateState,
    }),
    [stableAdditionalCommandContext],
  );
  const dispatch = useCommandDispatcher(context);

  const state = getState();
  return useMemo(
    () => ({
      ...state,
      dispatch,
    }),
    [state],
  );
};

/**
 * Workaround to get current state of command model via dispatch function in components
 *
 * USE ONLY WHEN ABSOLUTELY NECESSARY.
 * Normally, components should read state reactively from props/context
 * and commands can access cmdContext.getState() to get latest state before React update
 * to allow writing "sagas".
 *
 * However, there are rare scenarios where you need to write a saga-like chain in a component.
 * For example, we need to check result of save operation in a logic outside given command model
 * to decide whether to redirect or not, and implementing that with current means would lead
 * to excessively complex solutions.
 *
 * We intentionally don't expose `getState` to components directly
 * to avoid overusing it and rather choosing more clear reactive implementation.
 * If this shows to be a common scenarion in future, we'll rethink how to implement
 * such features with command model, but for now this workaround seems reasonable.
 *
 * @example
 * <Button onClick={() => {
 *   await model.dispatch(saveTheThing);
 *
 *   // we can't just read model.unsavedChanges because it has obsolete value.
 *   if (!workaroundExtractModelCurrentState(model.dispatch).unsavedChanges) {
 *     // save succeeded
 *     redirectSomewhere();
 *   }
 * >
 *  Save
 * </Button>
 */
export const workaroundExtractModelCurrentState = <S, O>(
  dispatch: CommandModelCommandDispatcher<S, unknown, O>,
): S & O => {
  let state!: S & O;
  dispatch((cmdContext) => () => {
    state = cmdContext.getState();
  });
  return state;
};
